import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC, useState } from 'react';
import styled from 'styled-components';

import FiltersDropdown from '@/components/FilterDropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import { FilterWrap } from '@/components/FilterDropdown/Parts';
import Text from '@/components/Text';
import Tooltiped from '@/components/Tooltiped';

const StyledFilterButton = styled(FilterButton)`
  margin: 0 !important;
  border-color: ${({ theme }) => theme.colors.grayscale};

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

const Column = styled.div`
  padding: 10px 0;
  padding-left: 15px;
  cursor: grab;
  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const StyledFiltersDropdown = styled(FiltersDropdown)`
  max-height: 340px;
`;

const SortableColumn: FC<{ title: string }> = ({ title }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: title });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Column ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Text $type="smallBold">{title}</Text>
    </Column>
  );
};

interface Props {
  columns: string[];
  setColumns: React.Dispatch<React.SetStateAction<string[]>>;
}

const SortColumns: FC<Props> = ({ columns, setColumns }) => {
  const [show, setShow] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setColumns((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over?.id as string);

        return arrayMove(items, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  return (
    <FilterWrap>
      <Tooltiped title="Reorder Table Columns">
        <StyledFilterButton
          text=""
          isActive={show}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
              width={18}
              height={18}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5"
              />
            </svg>
          }
          onClick={(e) => {
            e?.stopPropagation();
            setShow((prev) => !prev);
          }}
          filterApplied={false}
        />
      </Tooltiped>
      {show && (
        <StyledFiltersDropdown onClose={() => setShow(false)}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
          >
            <SortableContext
              items={columns}
              strategy={verticalListSortingStrategy}
            >
              {columns.map((column) => (
                <SortableColumn title={column} key={column} />
              ))}
            </SortableContext>
            <DragOverlay>
              {activeId ? <SortableColumn title={activeId} /> : null}
            </DragOverlay>
          </DndContext>
        </StyledFiltersDropdown>
      )}
    </FilterWrap>
  );
};

export default SortColumns;
